import React, { useMemo, useState } from 'react';
import {
	Button,
	Form,
	InputNumber,
	Modal,
	Popconfirm,
	Radio,
	Select,
	Spin,
	message,
} from 'antd';
import { RichTextFormField } from '../../components/RichTextEditor';
import ReviewIssuesField from './ReviewIssuesField';
import { isIssueWorthSaving, issueToDraft, caughtPhaseForReviewSlot } from './issueFields';
import type { ReviewIssueDraft } from './issueFields';
import {
	REVIEW_LOG_STATUS_LABELS,
	type ReviewLogStatus,
	buildReviewStatsPayload,
	reviewLogStatus,
} from './reviewStatus';
import { REVIEW_DURATION_HINT } from './fieldHints';
import { isReviewAuthor } from './authors';
import {
	useCreateProgramIssueMutation,
	useCreateProgramReviewMutation,
	useDeleteProgramIssueMutation,
	useDeleteProgramReviewMutation,
	useGetProgramIssuesQuery,
	useGetProgramQuery,
	useGetProgramReviewsQuery,
	useUpdateProgramIssueMutation,
	useUpdateProgramReviewMutation,
} from '../../redux/api/manufacturing';
import { useGetLoggedInUserQuery } from '../../redux/api/users';
import { useCamProgrammerUserOptions } from './useCamProgrammerUserOptions';
import { useProgramImages } from './useProgramImages';
import type {
	ProgramIssue,
	ProgramReview,
} from '../../interfaces/CamProgramming.interface';

interface ReviewFormValues {
	reviewer: string | null;
	review_status: ReviewLogStatus;
	review_duration_minutes: number | null;
	feedback: string;
	issues: ReviewIssueDraft[];
}

interface ReviewFormModalProps {
	programId: string;
	review: ProgramReview | null;
	open: boolean;
	allowReviewerOverride?: boolean;
	onClose: () => void;
}

interface ReviewFormDialogProps {
	programId: string;
	review: ProgramReview | null;
	allowReviewerOverride: boolean;
	onClose: () => void;
}

const reviewSlotLabel = (slotIndex: number) =>
	slotIndex === 0 ? 'first review' : 'secondary review';

/**
 * Full dialog mount keyed by create vs edit so the form never carries over state,
 * and so Cancel / Log live in the modal footer instead of the scrollable body.
 */
const ReviewFormDialog: React.FC<ReviewFormDialogProps> = ({
	programId,
	review,
	allowReviewerOverride,
	onClose,
}) => {
	const [form] = Form.useForm<ReviewFormValues>();
	const { userOptions: reviewerOptions, isLoading: isLoadingReviewers } =
		useCamProgrammerUserOptions({
			skip: !allowReviewerOverride,
			pinnedUsers: [review?.reviewer],
		});
	const { data: issueData, isLoading: isLoadingIssues } =
		useGetProgramIssuesQuery(
			{ program: programId, review: review?.id as string },
			{ skip: !review }
		);
	const { data: reviewsData } = useGetProgramReviewsQuery({ program: programId });
	const { data: program } = useGetProgramQuery(programId);
	const { data: currentUser } = useGetLoggedInUserQuery();
	const [createReview, { isLoading: isCreating }] =
		useCreateProgramReviewMutation();
	const [updateReview, { isLoading: isUpdating }] =
		useUpdateProgramReviewMutation();
	const [deleteReview, { isLoading: isDeleting }] =
		useDeleteProgramReviewMutation();
	const [createIssue] = useCreateProgramIssueMutation();
	const [updateIssue] = useUpdateProgramIssueMutation();
	const [deleteIssue] = useDeleteProgramIssueMutation();
	const [isSavingIssues, setIsSavingIssues] = useState(false);
	const images = useProgramImages(programId);

	const storedIssues: ProgramIssue[] = review ? issueData?.results || [] : [];
	const isSaving = isCreating || isUpdating || isSavingIssues || isDeleting;
	const canDeleteReview =
		!!review && isReviewAuthor(review, currentUser?.id);
	const reviewStatusValue = Form.useWatch('review_status', form);
	const isInProgress = reviewStatusValue === 'in_progress';

	const lockedCaughtPhase = useMemo(() => {
		const reviews = [...(reviewsData?.results ?? [])].sort((a, b) =>
			a.created_time.localeCompare(b.created_time)
		);
		const reviewIndex = review
			? reviews.findIndex((entry) => entry.id === review.id)
			: reviews.length;
		return caughtPhaseForReviewSlot(reviewIndex < 0 ? reviews.length : reviewIndex);
	}, [reviewsData, review]);

	const reviewSlotIndex = useMemo(() => {
		const reviews = [...(reviewsData?.results ?? [])].sort((a, b) =>
			a.created_time.localeCompare(b.created_time)
		);
		if (review) {
			const index = reviews.findIndex((entry) => entry.id === review.id);
			return index < 0 ? 0 : index;
		}
		return reviews.length;
	}, [reviewsData, review]);

	const saveIssues = async (reviewId: string, drafts: ReviewIssueDraft[]) => {
		const storedById = new Map(storedIssues.map((issue) => [issue.id, issue]));
		const keptIds = new Set(drafts.map((draft) => draft.id).filter(Boolean));

		const writes: Promise<unknown>[] = [];

		drafts.forEach((draft) => {
			const values = {
				severity: draft.severity,
				caught_phase: lockedCaughtPhase,
				fault: draft.fault,
				insufficient_training: draft.insufficient_training,
				description: draft.description,
			};
			const stored = draft.id ? storedById.get(draft.id) : undefined;
			if (!stored) {
				writes.push(
					createIssue({
						program: programId,
						review: reviewId,
						...values,
					}).unwrap()
				);
			} else if (
				stored.severity !== values.severity ||
				stored.caught_phase !== values.caught_phase ||
				stored.fault !== values.fault ||
				stored.insufficient_training !== values.insufficient_training ||
				(stored.description || '') !== values.description
			) {
				writes.push(updateIssue({ id: stored.id, programId, values }).unwrap());
			}
		});

		storedIssues
			.filter((issue) => !keptIds.has(issue.id))
			.forEach((issue) =>
				writes.push(deleteIssue({ id: issue.id, programId }).unwrap())
			);

		await Promise.all(writes);
	};

	const handleSubmit = async (values: ReviewFormValues) => {
		const payload = buildReviewStatsPayload(
			{
				review_status: values.review_status,
				review_duration_minutes: values.review_duration_minutes,
				reviewer: values.reviewer,
				feedback: values.feedback,
			},
			allowReviewerOverride,
			{
				isEdit: !!review,
				existingStartedAt: review?.review_started_at,
				existingCompletedAt: review?.review_completed_at,
			}
		);

		let reviewId: string;
		try {
			if (review) {
				await updateReview({
					id: review.id,
					programId,
					values: payload as Partial<ProgramReview>,
				}).unwrap();
				reviewId = review.id;
			} else {
				const created = await createReview({
					program: programId,
					...payload,
				} as Partial<ProgramReview> & { program: string }).unwrap();
				reviewId = created.id;
			}
		} catch {
			message.error('Failed to save review');
			return;
		}

		setIsSavingIssues(true);
		try {
			await saveIssues(
				reviewId,
				(values.issues || []).filter(isIssueWorthSaving)
			);
			message.success(review ? 'Review updated' : 'Review logged');
		} catch {
			message.warning('Review saved, but some issues could not be saved.');
		} finally {
			setIsSavingIssues(false);
			onClose();
		}
	};

	const handleDelete = async () => {
		if (!review) {
			return;
		}
		try {
			await deleteReview({ id: review.id, programId }).unwrap();
			message.success('Review removed');
			onClose();
		} catch {
			message.error('Failed to remove review');
		}
	};

	const modalTitle = review
		? `Edit ${reviewSlotLabel(reviewSlotIndex)}`
		: `Log ${reviewSlotLabel(reviewSlotIndex)}`;

	return (
		<Modal
			className="cam-modal"
			title={modalTitle}
			open
			centered
			onCancel={onClose}
			width={780}
			destroyOnClose
			footer={
				<div className="cam-modal__footer">
					<div>
						{canDeleteReview && (
							<Popconfirm
								title="Remove this review?"
								okText="Remove"
								okButtonProps={{ danger: true }}
								onConfirm={handleDelete}
							>
								<Button danger loading={isDeleting}>
									Remove
								</Button>
							</Popconfirm>
						)}
					</div>
					<div className="cam-modal__footer-actions">
						<Button onClick={onClose}>Cancel</Button>
						<Button
							type="primary"
							loading={isSaving}
							onClick={() => form.submit()}
						>
							{review ? 'Save' : 'Log'}
						</Button>
					</div>
				</div>
			}
		>
			{review && isLoadingIssues ? (
				<div className="cam-section__placeholder">
					<Spin />
				</div>
			) : (
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					initialValues={{
						reviewer: review?.reviewer?.id || null,
						review_status: reviewLogStatus(
							review?.review_started_at,
							review?.review_completed_at
						),
						review_duration_minutes: review?.review_duration_minutes ?? null,
						feedback: review?.feedback || '',
						issues: storedIssues.map(issueToDraft),
					}}
				>
					{allowReviewerOverride && (
						<Form.Item name="reviewer" label="Reviewer">
							<Select
								allowClear
								showSearch
								optionFilterProp="label"
								loading={isLoadingReviewers}
								placeholder="Defaults to you"
								options={reviewerOptions}
							/>
						</Form.Item>
					)}
					<Form.Item
						name="review_status"
						label="Review status"
						rules={[{ required: true, message: 'Choose a review status' }]}
					>
						<Radio.Group
							optionType="button"
							buttonStyle="solid"
							options={(
								Object.entries(REVIEW_LOG_STATUS_LABELS) as [
									ReviewLogStatus,
									string,
								][]
							).map(([value, label]) => ({ value, label }))}
						/>
					</Form.Item>
					{!isInProgress && (
						<Form.Item
							name="review_duration_minutes"
							label="Duration (min)"
							tooltip={REVIEW_DURATION_HINT}
							rules={[{ required: true, message: 'Enter the review time' }]}
						>
							<InputNumber
								min={0}
								style={{ width: '100%' }}
								placeholder="Minutes"
							/>
						</Form.Item>
					)}
					<Form.Item name="feedback" label="Feedback">
						<RichTextFormField
							{...images}
							placeholder="Review notes... Paste or drop images directly."
							minHeight={180}
						/>
					</Form.Item>
					<Form.Item name="issues">
						<ReviewIssuesField
							images={images}
							lockedCaughtPhase={lockedCaughtPhase}
							program={program}
						/>
					</Form.Item>
				</Form>
			)}
		</Modal>
	);
};

const ReviewFormModal: React.FC<ReviewFormModalProps> = ({
	programId,
	review,
	open,
	allowReviewerOverride = true,
	onClose,
}) => {
	if (!open) {
		return null;
	}

	return (
		<ReviewFormDialog
			key={review ? `edit-${review.id}` : 'create'}
			programId={programId}
			review={review}
			allowReviewerOverride={allowReviewerOverride}
			onClose={onClose}
		/>
	);
};

export default ReviewFormModal;
